import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Flex,
  Radio,
  Select,
  Skeleton,
  Tag,
  Typography
} from "antd"
import type { RangePickerProps } from "antd/lib/date-picker"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { getAlbums } from "../../Album/api/album.services.ts"
import { getAttachmentsFilterOptions } from "../../Album/api/attachment.services.ts"
import { type AttachmentFilterOptions } from "../../Album/types/attachment.types.ts"
import {
  RELATED_RECORD_TYPES,
  type RelatedRecordType
} from "../../Project/types/project-documents.types.ts"
import { FILE_FORMAT_CATEGORIES } from "../helpers/classify-file-format.ts"
import {
  DATE_RANGE_LABEL_KEYS,
  DATE_RANGE_PRESETS,
  type DateRangePreset
} from "../helpers/date-range-filter.ts"
import { documentTypeIconLookup } from "../helpers/document-type-visuals.ts"
import { buildFolderTree } from "../helpers/folder-tree.ts"
import { listEveryFolder } from "../helpers/list-every-folder.ts"
import {
  renderDocumentTypeDecoration,
  renderStatusDecoration
} from "../helpers/render-metadata-decoration.tsx"
import { useDocumentTypeOptions } from "../hooks/useDocumentTypeOptions.ts"
import { useRelatedRecordOptions } from "../hooks/useRelatedRecordOptions.ts"
import { useStatusOptions } from "../hooks/useStatusOptions.ts"
import {
  countActiveFilters,
  EMPTY_FILES_FILTERS,
  type FilesFilters,
  type OriginFilter
} from "../types/files-filters.types.ts"
import { type FolderListItem } from "../types/folder.types.ts"
import { FolderTreeSelect } from "./FolderTreeSelect.tsx"

const { Text } = Typography
const ANY_RECORD_TYPE = "any"
const ORIGIN_ALL = "all"

interface FilesFilterDrawerProps {
  open: boolean
  filters: FilesFilters
  /** Hides the Folder filter — the user is already browsing inside one. */
  hideFolderFilter?: boolean
  projectId: string
  projectName: string
  owners: string[]
  onClose: () => void
  onChange: (filters: FilesFilters) => void
  /** Lets the parent resolve filter values to labels for the chips row. */
  onOptionsLoaded?: (options: {
    fileTypes: { label: string; value: string }[]
    folders: { label: string; value: string }[]
    albums: { label: string; value: string }[]
    documentTypes: { label: string; value: string }[]
    statuses: { label: string; value: string }[]
    modelTypes: { label: string; value: string }[]
    customers: { label: string; value: string }[]
  }) => void
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Flex vertical gap={4}>
      <Text strong className="text-sm">
        {title}
      </Text>
      {children}
    </Flex>
  )
}

function RelatedRecordSection({
  projectId,
  projectName,
  draft,
  patch
}: {
  projectId: string
  projectName: string
  draft: FilesFilters
  patch: (next: Partial<FilesFilters>) => void
}) {
  const { t } = useTranslation()
  const recordType = draft.related_record_type
  const { options, loading, loadFailed } = useRelatedRecordOptions(
    recordType ?? "project",
    projectId,
    projectName
  )

  const selectType = (value: RelatedRecordType | typeof ANY_RECORD_TYPE) =>
    patch({
      related_record_type: value === ANY_RECORD_TYPE ? null : value,
      related_record_id: null,
      related_record_label: null
    })

  return (
    <FilterSection title={t("files.filters.relatedTo")}>
      <Flex vertical gap={6}>
        <Radio.Group
          aria-label={t("files.filters.relatedTo")}
          value={recordType ?? ANY_RECORD_TYPE}
          onChange={(e) => selectType(e.target.value)}
          options={[
            { label: t("files.filters.relatedToAny"), value: ANY_RECORD_TYPE },
            ...RELATED_RECORD_TYPES.map((type) => ({
              label: t(`files.linkRecord.types.${type}`),
              value: type
            }))
          ]}
        />
        {recordType &&
          (loading ? (
            <Skeleton.Input active block size="small" />
          ) : (
            <Select<string>
              showSearch
              allowClear
              aria-label={t("files.filters.relatedToRecord")}
              placeholder={t("files.linkRecord.searchPlaceholder")}
              notFoundContent={t(
                loadFailed ? "files.linkRecord.loadFailed" : "files.linkRecord.noRecords"
              )}
              value={draft.related_record_id ?? undefined}
              optionFilterProp="label"
              options={options}
              onChange={(value, option) => {
                const picked = Array.isArray(option) ? option[0] : option
                patch({
                  related_record_id: value ?? null,
                  related_record_label: value ? (picked?.label as string) : null
                })
              }}
              className="w-full"
            />
          ))}
      </Flex>
    </FilterSection>
  )
}

export function FilesFilterDrawer({
  open,
  filters,
  hideFolderFilter = false,
  projectId,
  projectName,
  owners,
  onClose,
  onChange,
  onOptionsLoaded
}: FilesFilterDrawerProps) {
  const { t } = useTranslation()
  const [options, setOptions] = useState<AttachmentFilterOptions | null>(null)
  const [folders, setFolders] = useState<FolderListItem[]>([])
  const [albums, setAlbums] = useState<{ id: string; name: string }[]>([])
  const { options: documentTypes, loadFailed: documentTypesFailed } =
    useDocumentTypeOptions(open)
  const documentTypeIcons = useMemo(() => documentTypeIconLookup(documentTypes), [documentTypes])
  const { options: statuses, loadFailed: statusesFailed } = useStatusOptions(open)
  const [draft, setDraft] = useState<FilesFilters>(filters)

  useEffect(() => {
    if (open) setDraft(filters)
    // Reset only when the drawer opens; `filters` is deliberately not a dependency.
  }, [open])

  useEffect(() => {
    if (!open || options) return
    let cancelled = false
    void getAttachmentsFilterOptions({ project_id: projectId }).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setOptions(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [open, options, projectId])

  useEffect(() => {
    if (!open || !projectId) return
    let cancelled = false
    void listEveryFolder({ project: projectId }).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setFolders(response.data.results || [])
    })
    void getAlbums({ project: projectId, page_size: 100 }).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setAlbums(response.data.results || [])
    })
    return () => {
      cancelled = true
    }
  }, [open, projectId])

  useEffect(() => {
    if (
      !options &&
      !folders.length &&
      !albums.length &&
      !documentTypes.length &&
      !statuses.length
    )
      return
    onOptionsLoaded?.({
      fileTypes: FILE_FORMAT_CATEGORIES.map((category) => ({
        label: t(`files.filters.fileTypeCategories.${category}`),
        value: category
      })),
      folders: folders.map((folder) => ({
        label: folder.name,
        value: folder.id
      })),
      albums: albums.map((album) => ({ label: album.name, value: album.id })),
      documentTypes,
      statuses,
      modelTypes: (options?.model_types ?? []).map((option) => ({
        label: String(option.label ?? option.value ?? ""),
        value: String(option.value ?? "")
      })),
      customers: (options?.customers ?? []).map((option) => ({
        label: String(option.label ?? option.value ?? ""),
        value: String(option.value ?? "")
      }))
    })
  }, [options, folders, albums, documentTypes, statuses, onOptionsLoaded, t])

  const patch = (next: Partial<FilesFilters>) => setDraft((prev) => ({ ...prev, ...next }))
  const activeCount = countActiveFilters(draft)
  const folderTree = buildFolderTree(folders)

  const handleCancel = () => {
    setDraft(filters)
    onClose()
  }

  const handleApply = () => {
    onChange(draft)
    onClose()
  }

  const onRangeChange: RangePickerProps["onChange"] = (_dates, dateStrings) => {
    patch({
      date_after: dateStrings?.[0] || null,
      date_before: dateStrings?.[1] || null
    })
  }

  return (
    <Drawer
      title={t("files.filters.title")}
      placement="right"
      width={360}
      open={open}
      onClose={handleCancel}
      extra={
        <Button
          type="link"
          size="small"
          disabled={activeCount === 0}
          onClick={() => setDraft(EMPTY_FILES_FILTERS)}
        >
          {t("files.filters.clearAll")}
        </Button>
      }
      footer={
        <Flex justify="end" gap="small">
          <Button onClick={handleCancel}>{t("files.filters.cancel")}</Button>
          <Button type="primary" onClick={handleApply}>
            {t("files.filters.apply")}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <FilterSection title={t("files.filters.origin")}>
          <Flex vertical gap={8}>
            <div>
              <Text type="secondary" className="text-xs">
                {t("files.filters.uploadedBy")}
              </Text>
              <Select
                className="w-full"
                value={draft.uploaded_by_origin ?? ORIGIN_ALL}
                onChange={(value: OriginFilter | typeof ORIGIN_ALL) =>
                  patch({ uploaded_by_origin: value === ORIGIN_ALL ? null : value })
                }
                options={[
                  { value: ORIGIN_ALL, label: t("files.filters.originAll") },
                  { value: "customer", label: t("files.filters.originCustomers") },
                  { value: "team", label: t("files.filters.originTeam") }
                ]}
              />
            </div>
            <div>
              <Text type="secondary" className="text-xs">
                {t("files.filters.createdBy")}
              </Text>
              <Select
                className="w-full"
                value={draft.created_by_origin ?? ORIGIN_ALL}
                onChange={(value: OriginFilter | typeof ORIGIN_ALL) =>
                  patch({ created_by_origin: value === ORIGIN_ALL ? null : value })
                }
                options={[
                  { value: ORIGIN_ALL, label: t("files.filters.originAll") },
                  { value: "customer", label: t("files.filters.originCustomers") },
                  { value: "team", label: t("files.filters.originTeam") }
                ]}
              />
            </div>
            <div>
              <Text type="secondary" className="text-xs">
                {t("files.filters.customer")}
              </Text>
              <Select
                mode="multiple"
                allowClear
                showSearch
                className="w-full"
                placeholder={t("files.filters.customerPlaceholder")}
                value={draft.customers}
                onChange={(value: string[]) => patch({ customers: value })}
                options={options?.customers ?? []}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          </Flex>
        </FilterSection>
        <FilterSection title={t("files.filters.fileType")}>
          <Flex gap={6} wrap="wrap">
            <Tag.CheckableTag
              checked={!draft.file_type}
              onChange={() => patch({ file_type: null })}
            >
              {t("files.filters.fileTypeCategories.all")}
            </Tag.CheckableTag>
            {FILE_FORMAT_CATEGORIES.map((category) => (
              <Tag.CheckableTag
                key={category}
                checked={draft.file_type === category}
                onChange={() =>
                  patch({ file_type: draft.file_type === category ? null : category })
                }
              >
                {t(`files.filters.fileTypeCategories.${category}`)}
              </Tag.CheckableTag>
            ))}
          </Flex>
        </FilterSection>

        <FilterSection title={t("files.filters.documentType")}>
          <Flex align="center" gap={6} className="w-full">
            {draft.document_type &&
              renderDocumentTypeDecoration(
                draft.document_type,
                documentTypeIcons[draft.document_type]
              )}
            <Select<string>
              showSearch
              allowClear
              aria-label={t("files.filters.documentType")}
              placeholder={t("files.filters.documentTypePlaceholder")}
              optionFilterProp="label"
              value={draft.document_type ?? undefined}
              onChange={(value) => patch({ document_type: value ?? null })}
              notFoundContent={
                documentTypesFailed ? t("files.documentType.loadFailed") : undefined
              }
              options={documentTypes}
              optionRender={(option) => (
                <Flex align="center" gap={6}>
                  {option.data.value
                    ? renderDocumentTypeDecoration(
                        String(option.data.value),
                        documentTypeIcons[String(option.data.value)]
                      )
                    : null}
                  {option.data.label}
                </Flex>
              )}
              style={{ flex: 1, minWidth: 0 }}
            />
          </Flex>
        </FilterSection>

        <FilterSection title={t("files.filters.status")}>
          <Flex align="center" gap={6} className="w-full">
            {draft.status && renderStatusDecoration(draft.status)}
            <Select<string>
              showSearch
              allowClear
              aria-label={t("files.filters.status")}
              placeholder={t("files.filters.statusPlaceholder")}
              optionFilterProp="label"
              value={draft.status ?? undefined}
              onChange={(value) => patch({ status: value ?? null })}
              notFoundContent={statusesFailed ? t("files.status.loadFailed") : undefined}
              options={statuses}
              optionRender={(option) => (
                <Flex align="center" gap={6}>
                  {option.data.value ? renderStatusDecoration(String(option.data.value)) : null}
                  {option.data.label}
                </Flex>
              )}
              style={{ flex: 1, minWidth: 0 }}
            />
          </Flex>
        </FilterSection>

        {!hideFolderFilter && (
          <FilterSection title={t("files.filters.folder")}>
            <Flex vertical gap={6}>
              <FolderTreeSelect
                allowClear
                aria-label={t("files.filters.folder")}
                placeholder={t("files.filters.folderPlaceholder")}
                treeData={folderTree}
                value={draft.folder ?? undefined}
                onChange={(value) => patch({ folder: value ?? null })}
              />
              {draft.folder && (
                <Checkbox
                  checked={draft.folder_include_subfolders}
                  onChange={(e) => patch({ folder_include_subfolders: e.target.checked })}
                >
                  {t("files.filters.includeSubfolders")}
                </Checkbox>
              )}
            </Flex>
          </FilterSection>
        )}

        <FilterSection title={t("files.filters.album")}>
          <Select<string>
            showSearch
            allowClear
            aria-label={t("files.filters.album")}
            placeholder={t("files.filters.albumPlaceholder")}
            optionFilterProp="label"
            value={draft.album ?? undefined}
            onChange={(value) => patch({ album: value ?? null })}
            options={albums.map((album) => ({ label: album.name, value: album.id }))}
            className="w-full"
          />
        </FilterSection>

        {owners.length > 0 && (
          <FilterSection title={t("files.filters.owner")}>
            <Select<string>
              showSearch
              allowClear
              aria-label={t("files.filters.owner")}
              placeholder={t("files.filters.ownerPlaceholder")}
              optionFilterProp="label"
              value={draft.owner ?? undefined}
              onChange={(value) => patch({ owner: value ?? null })}
              options={owners.map((owner) => ({ label: owner, value: owner }))}
              className="w-full"
            />
          </FilterSection>
        )}

        <RelatedRecordSection
          projectId={projectId}
          projectName={projectName}
          draft={draft}
          patch={patch}
        />

        <FilterSection title={t("files.filters.date")}>
          <Flex gap={6} wrap="wrap">
            <Select<DateRangePreset>
              aria-label={t("files.filters.date")}
              placeholder={t("files.filters.dateRangePresets.anyTime")}
              allowClear
              value={draft.date_range ?? undefined}
              onChange={(value) =>
                patch({
                  date_range: value ?? null,
                  ...(value === "custom" ? {} : { date_after: null, date_before: null })
                })
              }
              options={DATE_RANGE_PRESETS.map((preset) => ({
                label: t(`files.filters.dateRangePresets.${DATE_RANGE_LABEL_KEYS[preset]}`),
                value: preset
              }))}
              className="basis-40 grow"
            />
            {draft.date_range === "custom" && (
              <DatePicker.RangePicker
                value={
                  draft.date_after && draft.date_before
                    ? [dayjs(draft.date_after), dayjs(draft.date_before)]
                    : undefined
                }
                onChange={onRangeChange}
                className="basis-52 grow"
              />
            )}
          </Flex>
        </FilterSection>
      </Flex>
    </Drawer>
  )
}
